import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Clock, Share2, Tag, Swords } from "lucide-react";
import { calculateGuaranteedMatches } from "@/utils/eventUtils";

interface LeagueCardProps {
  league: {
    id: string;
    title: string;
    organizer?: string;
    sport: string;
    date: string;
    endDate?: string;
    location: string;
    registrationFee: number;
    genderCategories?: string[];
    subcategories?: Record<string, string[]>;
    imageUrl?: string;
    registrationDeadline?: string;
    matchType?: 'singles' | 'dobles' | 'ambos';
    leagueFormat?: string;
    total_weeks?: number;
    matches_per_week?: number;
    maxParticipants?: number;
    participants?: number;
    address?: string;
    postal_code?: string;
  };
  onLeagueClick?: (league: any) => void;
}

const LeagueCard = ({ league, onLeagueClick }: LeagueCardProps) => {
  
  const getDefaultLeagueImage = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'pádel':
      case 'padel':
        return '/lovable-uploads/ae890168-70e4-43bb-976d-e01eb8869c31.png';
      case 'tenis':
        return '/lovable-uploads/c9c992c0-9cbd-430e-9c26-ed615c5e47dc.png';
      case 'pickleball':
        return '/lovable-uploads/404ad11f-3a8c-4971-8c7f-b2f397498282.png';
      default:
        return '/lovable-uploads/bb567a48-6ee6-4eea-917e-c2e42d492ea0.png';
    }
  };

  const formatEventDate = (dateString: string, endDateString?: string) => {
    const startDate = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    
    if (endDateString) {
      const endDate = new Date(endDateString);
      const startFormatted = startDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      const endFormatted = endDate.toLocaleDateString('es-ES', options);
      return `${startFormatted} - ${endFormatted}`;
    }
    
    return startDate.toLocaleDateString('es-ES', options);
  };

  const calculateDaysUntilDeadline = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineDisplay = () => {
    if (!league.registrationDeadline) return null;
    
    const daysUntil = calculateDaysUntilDeadline(league.registrationDeadline);
    
    if (daysUntil < 0) {
      return { text: "Cerrado", color: "bg-gray-500" };
    } else if (daysUntil === 0) {
      return { text: "¡Último día!", color: "bg-red-500" };
    } else if (daysUntil <= 3) {
      return { text: `${daysUntil} día${daysUntil > 1 ? 's' : ''}`, color: "bg-red-500" };
    } else if (daysUntil <= 7) {
      return { text: `${daysUntil} días`, color: "bg-orange-500" };
    } else {
      return { text: `${daysUntil} días`, color: "bg-green-500" };
    }
  };

  const formatCategories = () => {
    if (!league.genderCategories || league.genderCategories.length === 0) {
      return "Masculino, Femenino";
    }
    return league.genderCategories.join(", ");
  };

  const isDoubles = league.matchType === 'dobles' || league.sport.toLowerCase() === 'pádel';
  const displayPrice = isDoubles ? Math.round(league.registrationFee / 2) : league.registrationFee;

  const getGuaranteedMatches = (): number => {
    // If we have explicit weeks and matches per week data, use that
    if (league.total_weeks && league.matches_per_week) {
      return league.total_weeks * league.matches_per_week;
    }
    
    // Otherwise use the calculation utility
    return calculateGuaranteedMatches(
      league.leagueFormat,
      true, // isLeague
      league.participants || 8,
      league.maxParticipants || 24,
      league.matchType
    );
  };

  const handleCardClick = () => {
    if (onLeagueClick) {
      onLeagueClick(league);
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const eventUrl = `${window.location.origin}/league/${league.id}`;
    const shareText = `¡Únete a ${league.title}! ${league.location} - ${formatEventDate(league.date)}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${eventUrl}`)}`;
    
    if (navigator.share) {
      navigator.share({
        title: league.title,
        text: shareText,
        url: eventUrl,
      }).catch(console.error);
    } else {
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleRegisterClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLeagueClick) {
      onLeagueClick(league);
    }
  };

  const deadlineDisplay = getDeadlineDisplay();
  const imageUrl = league.imageUrl || getDefaultLeagueImage(league.sport);

  return (
    <Card 
      className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer bg-white border-gray-200 group p-3 md:p-4"
      onClick={handleCardClick}
    >
      <div className="flex gap-3 md:gap-4">
        {/* League Image - LEFT SIDE with overlaid badges */}
        <div className="relative flex-shrink-0">
          {/* Status + Sport Badge Container */}
          <div className="absolute top-2 left-2 z-10 flex gap-1">
            {deadlineDisplay && deadlineDisplay.text !== "Cerrado" && (
              <Badge className={`${deadlineDisplay.color} text-white text-[10px] px-1.5 py-0.5`}>
                Próximo
              </Badge>
            )}
            <Badge className="bg-white/90 text-gray-800 text-[10px] px-1.5 py-0.5">
              {league.sport}
            </Badge>
          </div>
          <img 
            src={imageUrl} 
            alt={league.title}
            className="w-28 h-28 md:w-32 md:h-32 rounded-lg object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        {/* League Information - RIGHT SIDE */}
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
          {/* Header: Título + Precio */}
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm md:text-base line-clamp-2 leading-tight text-gray-900">
                {league.title}
              </h3>
              {league.organizer && (
                <p className="text-[10px] md:text-xs text-muted-foreground truncate">
                  por {league.organizer}
                </p>
              )}
            </div>
            {/* Precio destacado */}
            <div className="text-right flex-shrink-0">
              <span className="text-base md:text-lg font-bold text-primary">
                ${displayPrice.toLocaleString('es-MX')}
              </span>
              <span className="text-[10px] md:text-xs text-muted-foreground block">
                por persona
              </span>
            </div>
          </div>

          {/* Detalles de la liga */}
          <div className="space-y-0.5 text-xs md:text-sm text-muted-foreground">
            {/* Fechas */}
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 md:w-3.5 md:h-3.5 text-primary flex-shrink-0" />
              <span className="truncate">{formatEventDate(league.date, league.endDate)}</span>
            </div>
            
            {/* Límite de inscripción */}
            {deadlineDisplay && (
              <div className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 md:w-3.5 md:h-3.5 text-orange-500 flex-shrink-0" />
                <span>
                  Límite: <span className={`font-medium ${deadlineDisplay.text === "Cerrado" ? "text-gray-500" : deadlineDisplay.text.includes("día") && parseInt(deadlineDisplay.text) <= 3 ? "text-red-600" : "text-gray-700"}`}>
                    {deadlineDisplay.text}
                  </span>
                </span>
              </div>
            )}
            
            {/* Ubicación */}
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500 flex-shrink-0" />
              <span className="truncate">{league.location}</span>
            </div>
            
            {/* Encuentros garantizados */}
            <div className="flex items-center gap-1.5">
              <Swords className="w-3 h-3 md:w-3.5 md:h-3.5 text-purple-500 flex-shrink-0" />
              <span className="font-medium text-purple-600">{getGuaranteedMatches()} encuentros</span>
            </div>
          </div>

          {/* Footer: Categorías + Botones */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <Badge variant="outline" className="text-[10px] md:text-xs px-1.5 py-0 truncate max-w-[120px] md:max-w-[180px]">
              <Tag className="w-2.5 h-2.5 mr-1 flex-shrink-0" />
              {formatCategories()}
            </Badge>
            <div className="flex gap-1.5">
              <Button 
                size="sm" 
                className="h-7 text-xs px-2 md:px-3 bg-primary hover:bg-primary/90"
                onClick={handleRegisterClick}
              >
                Inscribirse
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 w-7 p-0"
                onClick={handleShare}
              >
                <Share2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LeagueCard;
